import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { executionDependencyNames } from '@de/utils/data-sources/readyWhen';

/**
 * Whether `name` is an own declared source key.
 *
 * Uses an own-property check rather than `in` so inherited keys
 * (`toString`, `constructor`, ...) are not treated as declared sources —
 * they never appear in `Object.keys(dataSources)` or the topological order,
 * so accepting them would desync the cascade set from `orderedNames`.
 */
function hasDeclaredSource(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  name: string,
): boolean {
  return Object.prototype.hasOwnProperty.call(dataSources, name);
}

/**
 * Compute the cascade set for an imperative refetch of `root`:
 * transitive declared dependencies ∪ `{root}` ∪ transitive dependents,
 * returned in Kahn topological order (each name at most once).
 *
 * Undeclared `dependsOn` names are ignored (same C5 degrade contract as
 * {@link topologicalSort}).
 */
export function cascadeSourceNames(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  orderedNames: readonly string[],
  root: string,
): readonly string[] {
  if (!hasDeclaredSource(dataSources, root)) {
    return [];
  }

  const deps = new Set<string>();
  const stack: string[] = [root];
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined) {
      break;
    }
    const decl = dataSources[current];
    for (const dep of executionDependencyNames(decl)) {
      if (!hasDeclaredSource(dataSources, dep)) {
        continue;
      }
      if (deps.has(dep)) {
        continue;
      }
      deps.add(dep);
      stack.push(dep);
    }
  }

  const dependents = new Set<string>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const name of orderedNames) {
      if (name === root || dependents.has(name)) {
        continue;
      }
      const declDeps = executionDependencyNames(dataSources[name]);
      for (const dep of declDeps) {
        if (dep === root || dependents.has(dep)) {
          dependents.add(name);
          grew = true;
          break;
        }
      }
    }
  }

  const cascade = new Set<string>([root]);
  deps.forEach((name) => {
    cascade.add(name);
  });
  dependents.forEach((name) => {
    cascade.add(name);
  });
  return orderedNames.filter((name) => cascade.has(name));
}
